/**
 * VideoCapturePage — Vídeo motor→escape (opcional, contrato v1.3 §1.1).
 *
 * O usuário grava até 30s: começa no motor, desce até o escape e TERMINA
 * com a câmera parada apontando para a saída (a análise de fumaça precisa
 * de uma janela estática final). Motor quente ou frio — o worker detecta:
 *   • Fumaça AZUL   → queima de óleo
 *   • Fumaça BRANCA persistente → aditivo/água (junta de cabeçote)
 *   • Fumaça BRANCA passageira → condensação normal (motor frio)
 *   • Fumaça PRETA  → mistura rica de combustível
 *   • Gotejamento ativo no motor
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Button,
  Card,
  Chip,
  Progress,
  Viewfinder,
  CaptureButton,
} from '../../components';
import { useInspectionContext } from '../../hooks/InspectionProvider';
import { formatFileSize } from '../../utils';

/** Duração máxima do vídeo (contrato v1.3 §1.1: ~15-30s) */
const MAX_DURATION_S = 30;

/** webm preferido — é o formato que o worker extrai via ffmpeg */
function pickMimeType(): string {
  const candidates = [
    'video/webm;codecs=vp8',
    'video/webm;codecs=vp9',
    'video/webm',
    'video/mp4',
  ];
  if (typeof MediaRecorder === 'undefined') return '';
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

function formatSeconds(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const VideoCapturePage: React.FC = () => {
  const navigate = useNavigate();
  const { state, captureVideo, removeVideo } = useInspectionContext();

  const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [stage, setStage] = useState<'idle' | 'recording' | 'review'>(
    state.video ? 'review' : 'idle'
  );
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const mediaSupported =
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    }
  }, []);

  const initCamera = useCallback(async () => {
    if (streamRef.current) return;
    setError(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = s;
      setStream(s);
      setPermissionState('granted');
    } catch (err) {
      console.error('Camera error:', err);
      setPermissionState('denied');
    }
  }, []);

  // Câmera liberada sempre que voltamos ao palco 'idle' (montagem + retake)
  useEffect(() => {
    if (stage === 'idle' && mediaSupported) {
      initCamera();
    }
  }, [stage, initCamera, mediaSupported]);

  // Auto-stop ao atingir a duração máxima
  useEffect(() => {
    if (stage === 'recording' && elapsed >= MAX_DURATION_S) {
      stopRecording();
    }
  }, [stage, elapsed, stopRecording]);

  // Cleanup no unmount — timer, recorder e stream
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      const rec = recorderRef.current;
      if (rec && rec.state !== 'inactive') {
        try {
          rec.stop();
        } catch {
          /* noop */
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const handleStartRecording = useCallback(() => {
    const s = streamRef.current;
    if (!s) {
      setError('Câmera não disponível');
      return;
    }

    const mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(s, { mimeType }) : new MediaRecorder(s);
    } catch {
      setError('Gravação de vídeo não suportada neste navegador');
      return;
    }

    chunksRef.current = [];
    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const type = mimeType || 'video/webm';
      const blob = new Blob(chunksRef.current, { type });
      const extension = type.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `engine_sweep.${extension}`, {
        type: blob.type,
        lastModified: Date.now(),
      });

      // Review mostra o vídeo gravado — libera a câmera
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setStream(null);
      }

      captureVideo(file);
      setStage('review');
    };
    recorder.onerror = () => {
      setError('Falha durante a gravação. Tente novamente.');
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setStage('idle');
    };

    recorder.start(1000);
    recorderRef.current = recorder;
    setElapsed(0);
    setStage('recording');
    timerRef.current = window.setInterval(() => {
      setElapsed((prev) => Math.min(prev + 1, MAX_DURATION_S));
    }, 1000);
  }, [captureVideo]);

  const handleRetake = () => {
    removeVideo();
    setElapsed(0);
    setError(null);
    setStage('idle');
  };

  const handleContinue = () => navigate('/inspecao/upload');

  const handleSkipVideo = () => {
    // Vídeo é opcional — descarta (se houver) e vai direto ao envio
    if (state.video) removeVideo();
    navigate('/inspecao/upload');
  };

  const handleBack = () => navigate('/inspecao/captura');

  // ============================================================
  // Review — vídeo gravado, decidir
  // ============================================================
  if (stage === 'review' && state.video) {
    return (
      <Page
        appBar={{
          title: 'Vídeo do Motor',
          subtitle: state.plate,
          leading: <span className="material-symbols-outlined">arrow_back</span>,
          leadingAction: handleBack,
          elevation: false,
          transparent: true,
        }}
        className="pb-24"
      >
        <div className="flex flex-col gap-4 px-4 max-w-[428px] mx-auto w-full">
          <Card variant="elevated" padding="md">
            <video
              src={state.video.preview}
              controls
              playsInline
              className="w-full rounded-xl bg-black aspect-video object-contain"
            />
            <div className="flex items-center justify-between mt-3">
              <Chip
                variant="success"
                size="sm"
                icon={<span className="material-symbols-outlined">check</span>}
              >
                Vídeo capturado
              </Chip>
              <span className="font-label-sm text-on-surface-variant">
                {formatFileSize(state.video.file.size)}
              </span>
            </div>
            {elapsed < 5 && (
              <p className="font-body-sm text-warning mt-2">
                Vídeo muito curto — recomendamos 15-30s para uma análise confiável.
              </p>
            )}
          </Card>

          <Card variant="outlined" padding="md">
            <p className="font-body-sm text-on-surface-variant">
              A IA vai analisar a fumaça do escape:{' '}
              <b>azul</b> (queima de óleo),{' '}
              <b>branca persistente</b> (aditivo/água — junta de cabeçote) e{' '}
              <b>preta</b> (mistura rica). Também detecta gotejamentos no motor.
            </p>
          </Card>

          <Button
            variant="primary"
            fullWidth
            onClick={handleContinue}
            leftIcon={<span className="material-symbols-outlined">cloud_upload</span>}
          >
            Continuar para o envio
          </Button>
          <Button
            variant="outline"
            fullWidth
            onClick={handleRetake}
            leftIcon={<span className="material-symbols-outlined">videocam</span>}
          >
            Gravar novamente
          </Button>
          <Button variant="ghost" fullWidth onClick={handleSkipVideo}>
            Enviar sem vídeo
          </Button>
        </div>
      </Page>
    );
  }

  // ============================================================
  // Idle / Recording — viewfinder ao vivo
  // ============================================================
  return (
    <Page
      appBar={{
        title: 'Vídeo do Motor',
        subtitle: state.plate,
        leading: <span className="material-symbols-outlined">arrow_back</span>,
        leadingAction: handleBack,
        elevation: false,
        transparent: true,
      }}
      className="h-screen pb-0"
    >
      {/* Por que este vídeo */}
      {stage === 'idle' && (
        <div className="px-4">
          <Card variant="elevated" padding="sm" className="mx-auto max-w-[428px]">
            <p className="font-body-sm text-on-surface-variant text-center">
              O vídeo habilita a análise de <b>fumaça</b> — detecção de{' '}
              <b>queima de óleo</b> (fumaça azul) e{' '}
              <b>vazamento de aditivo/água</b> (fumaça branca persistente).
            </p>
          </Card>
        </div>
      )}

      <div className="relative flex-1 max-w-[428px] mx-auto w-full">
        <Viewfinder
          stream={stream}
          corners={true}
          horizon={false}
          aspectRatio="16/10"
          overlay={
            <>
              {/* Indicador REC */}
              {stage === 'recording' && (
                <div className="absolute top-4 left-0 right-0 flex justify-center">
                  <Chip
                    variant="error"
                    size="sm"
                    icon={
                      <span className="material-symbols-outlined animate-pulse-soft">
                        radio_button_checked
                      </span>
                    }
                  >
                    REC {formatSeconds(elapsed)} / {formatSeconds(MAX_DURATION_S)}
                  </Chip>
                </div>
              )}

              {/* Instruções — visíveis antes de gravar */}
              {stage === 'idle' && (
                <div className="absolute bottom-28 left-4 right-4">
                  <Card variant="elevated" padding="md" className="glass-strong">
                    <p className="font-body-sm text-center text-on-surface">
                      Comece mostrando o <b>motor</b>, desça até o{' '}
                      <b>escape</b> e termine com a câmera{' '}
                      <b>parada</b> apontando para a saída.
                    </p>
                    <p className="font-label-sm text-on-surface-variant text-center mt-2">
                      Motor quente ou frio — até {MAX_DURATION_S} segundos
                    </p>
                  </Card>
                </div>
              )}

              {/* Progresso da gravação */}
              {stage === 'recording' && (
                <div className="absolute bottom-28 left-4 right-4">
                  <Progress
                    value={elapsed}
                    max={MAX_DURATION_S}
                    size="sm"
                    showLabel
                    label={`${elapsed}s de ${MAX_DURATION_S}s`}
                  />
                </div>
              )}
            </>
          }
        />

        {/* Erro de gravação */}
        {error && (
          <div className="absolute top-16 left-4 right-4">
            <Card variant="elevated" padding="sm" className="bg-error-container">
              <p className="font-body-sm text-on-error-container text-center">{error}</p>
            </Card>
          </div>
        )}

        {/* Controles */}
        <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col items-center gap-3">
          {stage === 'idle' && (
            <>
              <CaptureButton
                onCapture={handleStartRecording}
                disabled={permissionState !== 'granted'}
                size={80}
              />
              <Button variant="ghost" size="sm" onClick={handleSkipVideo}>
                Pular esta etapa
              </Button>
            </>
          )}
          {stage === 'recording' && (
            <button
              type="button"
              onClick={stopRecording}
              aria-label="Parar gravação"
              className="relative flex items-center justify-center rounded-full border-4 border-surface-container-lowest bg-error/90 backdrop-blur-md transition-all hover:bg-error w-20 h-20"
            >
              <span
                className="rounded-[4px] bg-on-surface"
                style={{ width: 26, height: 26 } as React.CSSProperties}
              />
            </button>
          )}
        </div>

        {/* Câmera negada ou gravação não suportada */}
        {(permissionState === 'denied' || !mediaSupported) && (
          <div className="absolute inset-0 flex items-center justify-center p-4 bg-inverse-surface z-10">
            <Card variant="elevated" padding="xl" className="text-center max-w-md">
              <span className="material-symbols-outlined text-error text-5xl mb-4 block">
                {mediaSupported ? 'videocam_off' : 'no_photography'}
              </span>
              <h3 className="font-headline-md">
                {mediaSupported ? 'Acesso à câmera negado' : 'Gravação não suportada'}
              </h3>
              <p className="font-body-md text-on-surface-variant mt-2 mb-4">
                {mediaSupported
                  ? 'Permita o acesso à câmera nas configurações do navegador para gravar o vídeo do motor.'
                  : 'Este navegador não suporta gravação de vídeo. Você pode continuar sem o vídeo.'}
              </p>
              <div className="flex flex-col gap-2">
                {mediaSupported && (
                  <Button variant="primary" onClick={initCamera}>
                    Tentar novamente
                  </Button>
                )}
                <Button variant="outline" onClick={handleSkipVideo}>
                  Continuar sem vídeo
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Page>
  );
};
